import React from 'react';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  noPadding?: boolean;
  variant?: 'default' | 'glass';
  hierarchy?: 'primary' | 'secondary' | 'interactive';
  density?: 'compact' | 'regular';
  selected?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onActivate?: () => void;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ noPadding = false, variant = 'default', hierarchy = 'secondary', density = 'regular', selected = false, disabled = false, loading = false, onActivate, className = '', children, onKeyDown, onClick, ...props }, ref) => {
    const variants = {
      default: 'bg-slate-800/50 border-slate-700',
      glass: 'bg-slate-800/30 backdrop-blur-md border-slate-700/50 shadow-lg shadow-black/10',
    };

    const hierarchyClasses = {
      primary: 'border-slate-600 bg-slate-800/70',
      secondary: '',
      interactive: 'border-slate-600 cursor-pointer transition-[transform,border-color,background-color] duration-150 hover:-translate-y-px hover:border-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 motion-reduce:transform-none',
    };
    const baseClasses = `rounded-lg border ${variants[variant]} ${hierarchyClasses[hierarchy]} ${selected ? 'border-brand-500/60 bg-brand-500/10 ring-1 ring-brand-500/30' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`;
    const paddingClass = noPadding ? '' : density === 'compact' ? 'p-4' : 'p-6';
    const interactive = hierarchy === 'interactive' && Boolean(onActivate);

    const activate = () => {
      if (!disabled && !loading) onActivate?.();
    };
    const cameFromNestedControl = (target: EventTarget | null, currentTarget: EventTarget) => {
      if (!(target instanceof Element) || target === currentTarget) return false;
      return Boolean(target.closest('button, a, input, select, textarea, [role="button"], [role="link"], [contenteditable="true"]'));
    };

    return (
      <div
        {...props}
        ref={ref}
        className={`${baseClasses} ${paddingClass} ${className}`}
        role={interactive ? 'button' : props.role}
        tabIndex={interactive ? (disabled ? -1 : 0) : props.tabIndex}
        aria-selected={selected || undefined}
        aria-disabled={disabled || undefined}
        aria-busy={loading || undefined}
        onClick={(event) => { onClick?.(event); if (!event.defaultPrevented && interactive && !cameFromNestedControl(event.target, event.currentTarget)) activate(); }}
        onKeyDown={(event) => {
          onKeyDown?.(event);
          if (!event.defaultPrevented && interactive && !cameFromNestedControl(event.target, event.currentTarget) && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            activate();
          }
        }}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

export const CardHeader: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', ...props }) => (
  <div className={`flex items-start justify-between gap-4 ${className}`} {...props} />
);
export const CardBody: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', ...props }) => (
  <div className={`mt-4 ${className}`} {...props} />
);
export const CardFooter: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', ...props }) => (
  <div className={`mt-4 border-t border-slate-700/70 pt-4 ${className}`} {...props} />
);
